// src/components/workflows/WorkflowCanvas.jsx - Main Workflow Interface with Mode Switcher
import React, { useEffect, useRef } from 'react';
import WorkflowEditor from './WorkflowEditor';
import WorkflowKanbanView from './WorkflowKanbanView';
import WorkflowDashboardView from './WorkflowDashboardView';
import useWorkflowStore from '../../stores/WorkflowStore';
import { Button } from '../ui/button.tsx';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select.tsx';
import { Switch } from '../ui/switch.tsx';
import { Edit, KanbanSquare, LayoutDashboard, Plus, AlertCircle, Play, Pause, Trash2, Check, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog.tsx';
import { Input } from '../ui/input.tsx';
import { Label } from '../ui/label.tsx';
import { Textarea } from '../ui/textarea.tsx';

/**
 * WorkflowCanvas - Top-level workflow interface with two modes
 *
 * Edit Mode:
 * - Traditional flowchart editor (React Flow)
 * - Design workflow structure with stage gates and actions
 * - Visual node-based interface (like n8n/Make)
 *
 * View Mode:
 * - Full-screen kanban board for operations
 * - Stage gates become kanban columns
 * - Drag customers between stages
 * - Real-time updates
 */
const WorkflowCanvas = () => {
  const {
    workflows,
    selectedWorkflow,
    mode,
    setMode,
    loadWorkflows,
    loadWorkflowDetails,
    createWorkflow,
    updateWorkflow,
    deleteWorkflow,
    creatingWorkflow,
    savingWorkflow,
    deletingWorkflow,
  } = useWorkflowStore();

  const [isCreateDialogOpen, setIsCreateDialogOpen] = React.useState(false);
  const [newWorkflowName, setNewWorkflowName] = React.useState('');
  const [newWorkflowDescription, setNewWorkflowDescription] = React.useState('');
  const [isConfirmingDelete, setIsConfirmingDelete] = React.useState(false);
  const [viewType, setViewType] = React.useState('kanban'); // 'kanban' or 'dashboard'

  // Canonical: Saving flag watchers
  const prevCreatingWorkflow = useRef(false);
  const prevDeletingWorkflow = useRef(false);

  // Create dialog — close on server confirmation
  useEffect(() => {
    if (prevCreatingWorkflow.current && !creatingWorkflow) {
      if (isCreateDialogOpen) {
        setIsCreateDialogOpen(false);
        setNewWorkflowName('');
        setNewWorkflowDescription('');
      }
    }
    prevCreatingWorkflow.current = creatingWorkflow;
  }, [creatingWorkflow, isCreateDialogOpen]);

  // Delete — reset confirm UI on server confirmation
  useEffect(() => {
    if (prevDeletingWorkflow.current && !deletingWorkflow) {
      setIsConfirmingDelete(false);
    }
    prevDeletingWorkflow.current = deletingWorkflow;
  }, [deletingWorkflow]);

  // Check if workflow has automation nodes (httpRequest, webhookReceiver, scheduleTrigger)
  const hasAutomationNodes = React.useMemo(() => {
    if (!selectedWorkflow?.flow_config?.nodes) return false;
    return selectedWorkflow.flow_config.nodes.some(
      n => ['httpRequest', 'webhookReceiver', 'scheduleTrigger'].includes(n.type)
    );
  }, [selectedWorkflow?.flow_config?.nodes]);

  // Workflow supports dashboard view if it has automation_config or automation nodes
  const supportsDashboard = selectedWorkflow?.automation_config || hasAutomationNodes;

  // For client_automation type, default to dashboard view
  React.useEffect(() => {
    if (selectedWorkflow?.workflow_type === 'client_automation') {
      setViewType('dashboard');
    }
  }, [selectedWorkflow?.id, selectedWorkflow?.workflow_type]);

  // Load workflows on mount
  useEffect(() => {
    loadWorkflows();
  }, [loadWorkflows]);

  // Handle workflow selection
  const handleWorkflowChange = (workflowId) => {
    loadWorkflowDetails(workflowId);
    setIsConfirmingDelete(false); // Reset delete confirmation when changing workflows
  };

  // Handle create workflow
  const handleCreateWorkflow = () => {
    if (!newWorkflowName.trim()) {
      return;
    }

    createWorkflow({
      name: newWorkflowName.trim(),
      description: newWorkflowDescription.trim(),
      is_active: true,
      // Don't send flow_config - let backend default to null
      // WorkflowEditor will create default entry node when loading
    });
    // Dialog close handled by creatingWorkflow flag watcher
  };

  // Handle cancel create workflow
  const handleCancelCreate = () => {
    setIsCreateDialogOpen(false);
    setNewWorkflowName('');
    setNewWorkflowDescription('');
  };

  // Handle toggle workflow active status
  const handleToggleActive = (isActive) => {
    if (!selectedWorkflow) return;

    updateWorkflow(selectedWorkflow.id, {
      is_active: isActive
    });
  };

  // Handle delete workflow
  const handleDeleteWorkflow = () => {
    if (!selectedWorkflow) return;

    deleteWorkflow(selectedWorkflow.id);
    // Confirm UI reset handled by deletingWorkflow flag watcher
  };

  return (
    <div className="w-full h-[calc(100vh-4rem)] bg-slate-950 relative">
      {/* Top Bar - Mode Switcher & Workflow Selector */}
      <div className="absolute top-0 left-0 right-0 z-20 bg-slate-900/95 border-b border-slate-700 backdrop-blur-sm">
        <div className="flex items-center justify-between px-6 py-3">
          {/* Left: Workflow Selector */}
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Select
                value={selectedWorkflow?.id || ''}
                onValueChange={handleWorkflowChange}
              >
                <SelectTrigger className="w-64 bg-slate-800 border-slate-700 text-slate-200">
                  <SelectValue placeholder="Select workflow..." />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700">
                  {workflows.map((workflow) => (
                    <SelectItem
                      key={workflow.id}
                      value={workflow.id}
                      className="text-slate-200"
                    >
                      {workflow.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Button
                size="sm"
                variant="outline"
                onClick={() => setIsCreateDialogOpen(true)}
                className="border-purple-500/30 text-purple-300 hover:bg-purple-500/10"
              >
                <Plus className="w-4 h-4 mr-1" />
                New Workflow
              </Button>
            </div>

            {selectedWorkflow && (
              <div className="text-sm text-slate-400 border-l border-slate-700 pl-4">
                {selectedWorkflow.description || 'No description'}
              </div>
            )}
          </div>

          {/* Center: Workflow Actions */}
          {selectedWorkflow && (
            <div className="flex items-center gap-3">
              {/* Active/Inactive Toggle */}
              <div className="flex items-center gap-3 px-4 py-2 bg-slate-800/50 rounded-lg border border-slate-700">
                <div className="flex items-center gap-2">
                  {selectedWorkflow.is_active ? (
                    <Play className="w-4 h-4 text-green-400" />
                  ) : (
                    <Pause className="w-4 h-4 text-slate-500" />
                  )}
                  <span className="text-sm font-medium text-slate-300">
                    {selectedWorkflow.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <Switch
                  checked={selectedWorkflow.is_active}
                  onCheckedChange={handleToggleActive}
                  disabled={savingWorkflow || deletingWorkflow}
                  className="data-[state=checked]:bg-green-600"
                />
              </div>

              {/* Delete Workflow Button */}
              {!isConfirmingDelete ? (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setIsConfirmingDelete(true)}
                  className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Delete
                </Button>
              ) : (
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={handleDeleteWorkflow}
                    disabled={deletingWorkflow}
                    className="bg-red-600 hover:bg-red-700"
                  >
                    <Check className="w-4 h-4 mr-1" />
                    Confirm
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setIsConfirmingDelete(false)}
                    className="border-slate-600 text-slate-300 hover:bg-slate-700"
                  >
                    <X className="w-4 h-4 mr-1" />
                    Cancel
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Right: Mode Switcher */}
          <div className="flex items-center gap-2 bg-slate-800 rounded-lg p-1">
            <Button
              size="sm"
              variant={mode === 'edit' ? 'default' : 'ghost'}
              onClick={() => setMode('edit')}
              className={`
                ${mode === 'edit'
                  ? 'bg-purple-600 hover:bg-purple-700 text-white'
                  : 'text-slate-400 hover:text-slate-200 hover:bg-slate-700'
                }
              `}
            >
              <Edit className="w-4 h-4 mr-2" />
              Edit Mode
            </Button>
            {/* View mode buttons - show both Kanban and Dashboard when workflow supports it */}
            {supportsDashboard ? (
              <>
                <Button
                  size="sm"
                  variant={mode === 'view' && viewType === 'kanban' ? 'default' : 'ghost'}
                  onClick={() => { setMode('view'); setViewType('kanban'); }}
                  className={`
                    ${mode === 'view' && viewType === 'kanban'
                      ? 'bg-purple-600 hover:bg-purple-700 text-white'
                      : 'text-slate-400 hover:text-slate-200 hover:bg-slate-700'
                    }
                  `}
                >
                  <KanbanSquare className="w-4 h-4 mr-2" />
                  Kanban
                </Button>
                <Button
                  size="sm"
                  variant={mode === 'view' && viewType === 'dashboard' ? 'default' : 'ghost'}
                  onClick={() => { setMode('view'); setViewType('dashboard'); }}
                  className={`
                    ${mode === 'view' && viewType === 'dashboard'
                      ? 'bg-purple-600 hover:bg-purple-700 text-white'
                      : 'text-slate-400 hover:text-slate-200 hover:bg-slate-700'
                    }
                  `}
                >
                  <LayoutDashboard className="w-4 h-4 mr-2" />
                  Dashboard
                </Button>
              </>
            ) : (
              <Button
                size="sm"
                variant={mode === 'view' ? 'default' : 'ghost'}
                onClick={() => setMode('view')}
                className={`
                  ${mode === 'view'
                    ? 'bg-purple-600 hover:bg-purple-700 text-white'
                    : 'text-slate-400 hover:text-slate-200 hover:bg-slate-700'
                  }
                `}
              >
                <KanbanSquare className="w-4 h-4 mr-2" />
                View Mode
              </Button>
            )}
          </div>
        </div>

        {/* Warning Banner - Active Workflow in Edit Mode */}
        {selectedWorkflow && selectedWorkflow.is_active && mode === 'edit' && (
          <div className="bg-amber-500/10 border-b border-amber-500/30 px-6 py-2">
            <div className="flex items-center gap-3 text-amber-200">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <div className="text-sm">
                <span className="font-semibold">Warning:</span> This workflow is currently active.
                Changes you make will affect ongoing executions immediately.
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Main Content Area */}
      <div className={`w-full h-full ${selectedWorkflow && selectedWorkflow.is_active && mode === 'edit' ? 'pt-24' : 'pt-16'}`}>
        {selectedWorkflow ? (
          <>
            {mode === 'edit' ? (
              <WorkflowEditor workflowId={selectedWorkflow.id} />
            ) : viewType === 'dashboard' && supportsDashboard ? (
              <WorkflowDashboardView workflow={selectedWorkflow} />
            ) : (
              <WorkflowKanbanView workflowId={selectedWorkflow.id} />
            )}
          </>
        ) : (
          <div className="flex items-center justify-center w-full h-full">
            <div className="text-center">
              <div className="text-2xl text-slate-400 mb-2">No workflow selected</div>
              <div className="text-sm text-slate-500 mb-4">
                Create a new workflow or select an existing one to get started
              </div>
              <Button
                onClick={() => setIsCreateDialogOpen(true)}
                className="bg-purple-600 hover:bg-purple-700 text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Workflow
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Create Workflow Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={(open) => { if (!creatingWorkflow) { if (!open) handleCancelCreate(); else setIsCreateDialogOpen(true); } }}>
        <DialogContent className="bg-slate-900 border-slate-700 text-slate-100">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold text-slate-100">
              Create New Workflow
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Workflow Name */}
            <div className="space-y-2">
              <Label htmlFor="workflow-name" className="text-slate-300">
                Workflow Name <span className="text-red-400">*</span>
              </Label>
              <Input
                id="workflow-name"
                placeholder="e.g., Customer Onboarding, Lead Nurture"
                value={newWorkflowName}
                onChange={(e) => setNewWorkflowName(e.target.value)}
                className="bg-slate-800 border-slate-700 text-slate-100 placeholder:text-slate-500"
                autoFocus
              />
            </div>

            {/* Workflow Description */}
            <div className="space-y-2">
              <Label htmlFor="workflow-description" className="text-slate-300">
                Description (optional)
              </Label>
              <Textarea
                id="workflow-description"
                placeholder="Brief description of this workflow"
                value={newWorkflowDescription}
                onChange={(e) => setNewWorkflowDescription(e.target.value)}
                rows={3}
                className="bg-slate-800 border-slate-700 text-slate-100 placeholder:text-slate-500"
              />
            </div>

            {/* Info */}
            <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-3">
              <div className="text-xs text-purple-300">
                <strong>Tip:</strong> You can switch between Edit Mode (design the workflow) and View Mode (operate the kanban board) at any time.
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={handleCancelCreate}
              disabled={creatingWorkflow}
              className="bg-slate-800 border-slate-700 text-slate-300 hover:bg-slate-700"
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateWorkflow}
              disabled={!newWorkflowName.trim() || creatingWorkflow}
              className="bg-purple-600 hover:bg-purple-700 text-white"
            >
              {creatingWorkflow ? 'Creating...' : 'Create Workflow'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WorkflowCanvas;
